// Command Palette — accès rapide universel par Ctrl+K (ou Cmd+K sur Mac)
// Permet de naviguer instantanément entre écrans, sous-vues RH et employés
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search, Command, ArrowUp, ArrowDown, CornerDownLeft, X,
  Home, Clock, Calendar, MessageSquare, PartyPopper, CheckSquare, Folder, UserCog, Car,
  Users, UserPlus, CalendarCheck, FileText, TrendingUp, ClipboardList, Download
} from 'lucide-react';

const NAV_ITEMS = [
  // Écrans principaux
  { id: 'go:dashboard',  type: 'page', label: 'Tableau de bord', shortcut: 'D', view: 'dashboard',  Icon: Home,         roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'] },
  { id: 'go:pointeuse',  type: 'page', label: 'Pointeuse',       shortcut: 'P', view: 'pointeuse',  Icon: Clock,        roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'] },
  { id: 'go:planning',   type: 'page', label: 'Planning',                       view: 'planning',   Icon: Calendar,     roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },
  { id: 'go:messagerie', type: 'page', label: 'Messagerie',                     view: 'messagerie', Icon: MessageSquare,roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'] },
  { id: 'go:evenements', type: 'page', label: 'Événements',                     view: 'evenements', Icon: PartyPopper,  roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'] },
  { id: 'go:taches',     type: 'page', label: 'Tâches',                         view: 'taches',     Icon: CheckSquare,  roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'] },
  { id: 'go:projets',    type: 'page', label: 'Projets',                        view: 'projets',    Icon: Folder,       roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },
  { id: 'go:rh',         type: 'page', label: 'RH',              shortcut: 'R', view: 'rh',         Icon: UserCog,      roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },
  { id: 'go:vehicules',  type: 'page', label: 'Véhicules',                      view: 'vehicules',  Icon: Car,          roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },

  // Sous-vues RH (raccourci direct)
  { id: 'rh:employees',    type: 'rh', label: 'RH → Employés',         view: 'rh', rhSubView: 'employees',    Icon: Users,        roles: ['Patron', 'Super Manager', 'Manager'] },
  { id: 'rh:inscriptions', type: 'rh', label: 'RH → Inscriptions',     view: 'rh', rhSubView: 'inscriptions', Icon: UserPlus,     roles: ['Patron', 'Super Manager'] },
  { id: 'rh:conges',       type: 'rh', label: 'RH → Congés',           view: 'rh', rhSubView: 'conges',       Icon: Calendar,     roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },
  { id: 'rh:solde_cp',     type: 'rh', label: 'RH → Solde CP',         view: 'rh', rhSubView: 'solde_cp',     Icon: CalendarCheck,roles: ['Patron', 'Super Manager', 'Manager'] },
  { id: 'rh:note_rh',      type: 'rh', label: 'RH → Note RH Congés',   view: 'rh', rhSubView: 'note_rh',      Icon: FileText,     roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },
  { id: 'rh:modulation',   type: 'rh', label: 'RH → Modulation',       view: 'rh', rhSubView: 'modulation',   Icon: TrendingUp,   roles: ['Patron', 'Super Manager', 'Manager'] },
  { id: 'rh:documents',    type: 'rh', label: 'RH → Documents RH',     view: 'rh', rhSubView: 'documents',    Icon: FileText,     roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'] },
  { id: 'rh:demandes',     type: 'rh', label: 'RH → Demandes',         view: 'rh', rhSubView: 'demandes',     Icon: ClipboardList,roles: ['Patron', 'Super Manager', 'Manager', 'Employé'] },
  { id: 'rh:export_paie',  type: 'rh', label: 'RH → Export Paie',      view: 'rh', rhSubView: 'export_paie',  Icon: Download,     roles: ['Patron', 'Super Manager'] },
  { id: 'rh:export_hcr',   type: 'rh', label: 'RH → Export HCR',       view: 'rh', rhSubView: 'export_hcr',   Icon: Download,     roles: ['Patron', 'Super Manager'] },
];

// Normalise un texte pour recherche fuzzy (accents, casse)
const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const CommandPalette = ({ open, onClose, onNavigate, onNavigateRH, role, employees = [] }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset à chaque ouverture
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // Construire la liste filtrée selon rôle et requête
  const results = useMemo(() => {
    const navItems = NAV_ITEMS.filter(i => i.roles.includes(role));
    const empResults = (employees || [])
      .filter(e => !e.deleted_at)
      .map(e => ({
        id: `emp:${e.id}`,
        type: 'employee',
        label: `${e.first_name} ${e.last_name}`,
        subtitle: e.role_name + (e.service_name ? ` · ${e.service_name}` : ''),
        avatar: e.avatar,
        view: 'rh',
        rhSubView: 'employees',
        Icon: Users,
        roles: ['Patron', 'Super Manager', 'Manager'],
      }))
      .filter(i => i.roles.includes(role));

    const all = [...navItems, ...empResults];

    if (!query.trim()) return navItems; // Quand vide : nav uniquement

    const q = normalize(query);
    const scored = all
      .map(item => {
        const haystack = normalize(`${item.label} ${item.subtitle || ''}`);
        const idx = haystack.indexOf(q);
        if (idx === -1) return null;
        // Score : proximité du début + courte distance
        return { item, score: idx + (haystack.length - q.length) / 100 };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score)
      .slice(0, 30)
      .map(s => s.item);

    return scored;
  }, [query, role, employees]);

  // Garder activeIndex dans les bornes
  useEffect(() => {
    if (activeIndex >= results.length) setActiveIndex(Math.max(0, results.length - 1));
  }, [results, activeIndex]);

  // Faire scroller l'élément actif
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Gestion clavier
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(results.length - 1, i + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); selectItem(results[activeIndex]); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, activeIndex]);

  const selectItem = (item) => {
    if (!item) return;
    if (item.rhSubView) {
      onNavigateRH(item.view, item.rhSubView);
    } else {
      onNavigate(item.view);
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-[10vh] bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Recherche rapide"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl bg-elevated-2 rounded-xl shadow-2xl border border-border-subtle overflow-hidden">
        {/* Header de saisie */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
          <Search className="w-5 h-5 text-text-muted flex-shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            placeholder="Rechercher un écran, une sous-vue, un employé..."
            className="flex-1 bg-transparent border-none focus:outline-none text-text-primary placeholder:text-text-muted text-base"
            aria-label="Recherche"
          />
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-text-muted hover:text-text-primary rounded-md min-h-[32px] min-w-[32px] flex items-center justify-center"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Liste de résultats */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-text-muted">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" aria-hidden="true" />
              <p className="text-sm">Aucun résultat pour <strong>« {query} »</strong></p>
            </div>
          ) : (
            <ul className="py-1">
              {results.map((item, idx) => {
                const Icon = item.Icon;
                const isActive = idx === activeIndex;
                return (
                  <li key={item.id} data-idx={idx}>
                    <button
                      type="button"
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isActive ? 'bg-indigo-600 text-white' : 'text-text-primary hover:bg-elevated-1'
                      }`}
                    >
                      {item.avatar ? (
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          isActive ? 'bg-white/20 text-white' : 'bg-gradient-to-br from-purple-400 to-purple-600 text-white'
                        }`}>
                          {item.avatar}
                        </span>
                      ) : Icon ? (
                        <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-text-muted'}`} aria-hidden="true" />
                      ) : null}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{item.label}</div>
                        {item.subtitle && (
                          <div className={`text-xs truncate ${isActive ? 'text-indigo-100' : 'text-text-muted'}`}>
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                      {item.shortcut && !isActive && (
                        <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-elevated-1 border border-border-subtle rounded text-text-muted">
                          {item.shortcut}
                        </kbd>
                      )}
                      {isActive && (
                        <CornerDownLeft className="w-4 h-4 text-white" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer raccourcis clavier */}
        <div className="hidden sm:flex items-center justify-between gap-3 px-4 py-2 border-t border-border-subtle bg-elevated-1 text-xs text-text-muted">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" />Naviguer</span>
            <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" />Ouvrir</span>
            <span><kbd className="px-1 font-mono bg-elevated-2 border border-border-subtle rounded">Échap</kbd> Fermer</span>
          </div>
          <span className="flex items-center gap-1"><Command className="w-3 h-3" />K</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
